import React, { useEffect, useState } from "react";
import {
  Toolbar,
  Stack,
  Box,
  Button,
  useScrollTrigger,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { handleLogin } from "../../services/authService";
import { useTranslation } from "react-i18next";
import { LogoButton } from "../main/LogoButton";
import { AuthUserButton } from "../main/AuthUserButton";
import { LanguageSelection } from "../main/LanguageSelection";
import { getDashboardUrl, getDashboardLabel } from "../../utils/navigationHelper";

/**
 * This Header is:
 * - Transparent at top (0 scroll).
 * - White w/ shadow after scrolling.
 * - Link text color also changes to remain visible.
 */
export const Header = () => {
  const theme = useTheme();
  const { t } = useTranslation(['landing', 'common']);
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const trigger = useScrollTrigger({ 
    threshold: 80,
    disableHysteresis: true,
    disabled: false
  });
  
  const [isAtTop, setIsAtTop] = useState(true);
  const [scrollingUp, setScrollingUp] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsAtTop(currentScrollY === 0);
      setScrollingUp(currentScrollY < lastScrollY);
      setLastScrollY(currentScrollY);
      
      // Dynamically update body class for PWA safe area background
      if (currentScrollY > 80) {
        document.body.classList.add('header-solid');
      } else {
        document.body.classList.remove('header-solid');
      }
    };
    
    setIsAtTop(window.scrollY === 0);
    
    // Set initial state
    if (window.scrollY > 80) {
      document.body.classList.add('header-solid');
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clean up on unmount
      document.body.classList.remove('header-solid');
    };
  }, [lastScrollY]);

  // Handle MSAL redirect if needed
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        await instance.handleRedirectPromise();
      } catch (err) {
        console.error("MSAL redirect error:", err);
      }
    };
    handleRedirect();
  }, [instance]);

  // Always use solid background for better visibility, especially in PWA
  const backgroundColor = trigger ? "#ffffff" : "rgba(255, 255, 255, 0.95)";
  const boxShadow = trigger && !scrollingUp && !isAtTop ? 'rgba(0, 0, 0, 0.05) 0px 1px 2px' : "rgba(0, 0, 0, 0.02) 0px 1px 1px";
  const linkColor = theme.palette.text.primary; // Always use dark text for visibility

  const navigationItems = [
    { key: 'about', label: t('navigation.about', { ns: 'common' }), href: '#about-section' },
    { key: 'howItWorks', label: t('navigation.howItWorks', { ns: 'common' }), href: '#how-it-works-section' },
    { key: 'benefits', label: t('navigation.benefits', { ns: 'common' }), href: '#benefits-section' },
    { key: 'pricing', label: t('navigation.pricing', { ns: 'common' }), href: '#pricing-section' }
  ];

  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <Toolbar
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.components.MuiLayout.defaultProps.zIndex.header,
        minHeight: { xs: 56, sm: 64, md: 72 },
        px: { xs: 1.5, sm: 3, md: 4 },
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        backgroundColor,
        boxShadow,
        backdropFilter: 'blur(8px)', // Always apply backdrop blur for better text visibility
        borderBottom: '1px solid',
        borderColor: trigger ? 'divider' : 'rgba(0, 0, 0, 0.08)',
        // iOS PWA safe area support - extend header to fill safe area
        '@media (display-mode: standalone)': {
          paddingTop: 'max(env(safe-area-inset-top), 20px)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          minHeight: {
            xs: `calc(56px + max(env(safe-area-inset-top), 20px))`,
            sm: `calc(64px + max(env(safe-area-inset-top), 20px))`,
            md: `calc(72px + max(env(safe-area-inset-top), 20px))`
          },
          // Ensure background extends to fill the entire header area including safe area
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-100px', // Extend above to cover safe area
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: trigger ? '#ffffff' : 'rgba(255, 255, 255, 0.95)',
            zIndex: -1
          }
        },
        // Android adjustments
        '@media (display-mode: standalone) and (orientation: portrait)': {
          paddingTop: 'max(env(safe-area-inset-top), 10px)',
          minHeight: {
            xs: `calc(56px + max(env(safe-area-inset-top), 10px))`,
            sm: `calc(64px + max(env(safe-area-inset-top), 10px))`,
            md: `calc(72px + max(env(safe-area-inset-top), 10px))`
          }
        }
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          width: "100%",
          maxWidth: 'lg',
          mx: 'auto'
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexShrink: 1
        }}>
          <LogoButton />
        </Box>

        {/* Navigation Links - Desktop Only */}
        <Box sx={{ 
          display: { xs: 'none', lg: 'flex' }, // Changed from md to lg for better spacing
          alignItems: 'center',
          gap: { lg: 1.5, xl: 2.5 }, // Reduced gap for better fit
          flexGrow: 1,
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          {navigationItems.map((item) => (
            <Button
              key={item.key}
              component="button"
              sx={{
                color: linkColor,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { lg: '0.85rem', xl: '0.95rem' }, // Smaller font for better fit
                px: { lg: 0.75, xl: 1.25 }, // Reduced padding
                py: 0.5,
                minWidth: 'auto',
                borderRadius: 1,
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                maxWidth: '140px', // Prevent buttons from getting too wide
                '&:hover': {
                  backgroundColor: trigger ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.href);
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* Mobile/Tablet Menu Button */}
        <Box sx={{ 
          display: { xs: 'flex', lg: 'none' },
          alignItems: 'center',
          flexGrow: 1,
          justifyContent: 'center'
        }}>
          <IconButton
            onClick={toggleMobileMenu}
            sx={{
              color: linkColor,
              padding: 1
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Stack 
          direction="row" 
          spacing={{ xs: 0.5, sm: 1, lg: 2 }} // Reduced spacing on smaller screens
          alignItems="center"
          flexShrink={0}
        >
          {isAuthenticated && (
            <Button
              variant="text"
              href={getDashboardUrl()}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: linkColor,
                fontSize: { xs: '0.8rem', sm: '0.875rem', lg: '1rem' },
                py: { xs: 0.5, sm: 1 },
                px: { xs: 0.75, sm: 1, lg: 1.5 },
                minWidth: { xs: 'auto', sm: '70px', lg: '80px' },
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: trigger ? 'action.hover' : 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {getDashboardLabel(t)}
            </Button>
          )}

          <LanguageSelection
            sx={{
              display: 'flex',
              alignItems: 'center',
              '& .MuiIconButton-root': {
                color: linkColor,
                transition: 'all 0.3s ease',
                padding: { xs: 0.75, sm: 1, lg: 1.5 }, // Reduced padding
                visibility: 'visible',
                opacity: 1,
                display: 'flex'
              }
            }}
          />

          {isAuthenticated ? (
            <AuthUserButton headerTriggered={trigger} />
          ) : (
            <Button
              onClick={() => handleLogin(instance)}
              variant="contained"
              color="primary"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: 'primary.main',
                color: 'white',
                fontSize: { xs: '0.8rem', sm: '0.875rem', lg: '1rem' },
                py: { xs: 0.5, sm: 1 },
                px: { xs: 1, sm: 1.5, lg: 2 },
                minWidth: { xs: 'auto', sm: '70px', lg: '80px' },
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              {t("header.buttons.login", { ns: 'common' })}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Mobile/Tablet Drawer Menu */}
      <Drawer
        anchor="top"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: trigger ? '#ffffff' : theme.palette.primary.main,
            color: trigger ? theme.palette.text.primary : '#ffffff',
            mt: { xs: 7, sm: 8, md: 9 }, // Account for header height
            borderRadius: '0 0 16px 16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <Box sx={{ pt: 2, pb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, mb: 2 }}>
            <Box /> {/* Spacer */}
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: trigger ? theme.palette.text.primary : '#ffffff' }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          
          <List sx={{ px: 2 }}>
            {navigationItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  onClick={() => handleNavClick(item.href)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: trigger ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <ListItemText 
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            
            <Divider sx={{ my: 2, backgroundColor: trigger ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.2)' }} />
            
            {isAuthenticated && (
              <ListItem disablePadding>
                <ListItemButton
                  component="a"
                  href={getDashboardUrl()}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: trigger ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <ListItemText 
                    primary={getDashboardLabel(t)}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
            
            {!isAuthenticated && (
              <ListItem disablePadding sx={{ mt: 2 }}>
                <Button
                  onClick={() => {
                    handleLogin(instance);
                    setMobileMenuOpen(false);
                  }}
                  variant={trigger ? "contained" : "outlined"}
                  color={trigger ? "primary" : "primary"}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: trigger ? 'primary.main' : 'primary.main',
                    color: trigger ? 'inherit' : 'primary.main',
                    fontSize: '1rem',
                    py: 1.5,
                    mx: 2,
                    borderRadius: 2
                  }}
                >
                  {t("header.buttons.login", { ns: 'common' })}
                </Button>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </Toolbar>
  );
};
